import React from "react"
import { useSignal } from "@preact/signals-react"
import { AutocompleteAjaxSeeker, IconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"
import { AppBulkEditAction, AppBulkEditDialog, AppSelectionBar, AppSelectionBarButton } from "@components"
import { BACKEND_APIS } from "@constants"
import { useToast } from "@hooks"
import { autoEuropeListData } from "Pages/AutoEuropeTool/Page/@signals"

const toOption = ({ id, name }) => ({ value: id, label: name })

// The selection bar of the pricing tools: one action, the season set for every selected tool, picked in a dialog
export default function BulkSeasonSetToolbar({ selectedData, setSelectedRows }) {
	const seasonSet = useSignal(null)
	const isLoading = useSignal(false)
	const isOpen = useSignal(false)
	const { showToast } = useToast()

	// TableSeeker already maps selectedData to the actual row objects array
	const count = selectedData?.length ?? 0

	const handleClose = () => {
		isOpen.value = false
		seasonSet.value = null
	}

	const handleApply = async () => {
		if (!seasonSet.value) return
		const selected = (selectedData || []).filter(Boolean)
		const newSetId = seasonSet.value

		isLoading.value = true
		try {
			const updated = await postSubmit(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/edit-season-set`, {
				ids: selected.map(t => t.id),
				seasonSetId: newSetId,
			})
			const updatedById = Object.fromEntries((updated || []).map(t => [t.id, t]))
			autoEuropeListData.value = autoEuropeListData.value.map(t =>
				updatedById[t.id] ? updatedById[t.id] : t
			)
			showToast.success(`Season set updated for ${selected.length} tool${selected.length > 1 ? 's' : ''}`)
			setSelectedRows([])
			handleClose()
		} catch (e) {
			const body = await e?.json?.().catch(() => null)
			showToast.error(body?.error_msg || "Error updating season set")
		}
		isLoading.value = false
	}

	return <>
		<AppSelectionBar count={count} onClear={() => setSelectedRows([])}>
			<AppSelectionBarButton icon="calendar-days" label="Season Set" onClick={() => isOpen.value = true}/>
		</AppSelectionBar>
		<AppBulkEditDialog
			open={isOpen.value}
			onClose={handleClose}
			icon="calendar-days"
			title="Season Set"
			maxWidth="sm"
			actions={<AppBulkEditAction
				loading={isLoading.value}
				startIcon={<IconSeeker icon="floppy-disk"/>}
				disabled={!seasonSet.value || isLoading.value}
				onClick={handleApply}
			>
				{isLoading.value ? 'Applying…' : `Apply to ${count} tool${count !== 1 ? 's' : ''}`}
			</AppBulkEditAction>}
		>
			<AutocompleteAjaxSeeker
				label="Season Set"
				placeholder="Select a season set"
				urlAjax={`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/season-sets/list`}
				onAjax={options => options.map(toOption)}
				value={seasonSet.value}
				onChange={e => { seasonSet.value = e.target.value }}
			/>
		</AppBulkEditDialog>
	</>
}
